import {
  Box,
  BoxProps,
  Checkbox,
  CheckboxProps,
  ExpandableSection,
} from '@amzn/awsui-components-core';
import React from 'react';
import AppContextProps from '../../contexts/AppContextProps';
import { ContentType, TechDomainDisplayNames } from '@amzn/awscat-pattern-library-service-model';
import {
  awsServiceReadonlyDisplayNames,
  contentRequestStateDisplayNames,
  contentRequestTypeDisplayNames,
  contentTypeDisplayNames,
  environmentDisplayNames,
  publicationStateDisplayNames,
  rTypeDisplayNames,
  workloadDisplayNames,
} from '../utils/DisplayNames';
import { publicationStateVisibilityValues } from '../utils/SelectableOptions';

export enum FilterCategory {
  ContentType = 'contentType',
  Code = 'code',
  State = 'state',
  RequestType = 'requestType',
  TechDomains = 'techDomains',
  RType = 'rType',
  Workload = 'workload',
  AwsServices = 'awsServices',
  Environment = 'environment',
  Authors = 'authors',
  RequireCode = 'requireCode',
  IncludeCode = 'includeCode',
  PublicationState = 'publicationState',
  IsFavoriteCollection = 'isFavoriteCollection',
  BarRaiserReviewStatus = 'barRaiserReviewStatus',
}

export const filterOptionToLabelMap = new Map<FilterCategory, string>([
  [FilterCategory.BarRaiserReviewStatus, 'Bar raiser review status'],
  [FilterCategory.ContentType, 'Content type'],
  [FilterCategory.Code, 'Code'],
  [FilterCategory.State, 'Status'],
  [FilterCategory.RequestType, 'Suggestion type'],
  [FilterCategory.TechDomains, 'Technology'],
  [FilterCategory.RType, 'Migration scenario'],
  [FilterCategory.Workload, 'Workload'],
  [FilterCategory.AwsServices, 'AWS service'],
  [FilterCategory.Environment, 'Planned use'],
  [FilterCategory.Authors, 'Authors'],
  [FilterCategory.RequireCode, 'Require code'],
  [FilterCategory.IncludeCode, 'Include code'],
  [FilterCategory.PublicationState, 'Visibility'],
  [FilterCategory.IsFavoriteCollection, 'Favorite'],
]);

export interface ContentFiltersProps extends AppContextProps {
  isLoading: boolean;
  filterOptionToMetadataMap: Map<string, string>;
  filterOptionToValuesMap: Map<string, string[]>;
  onFilterChanged: (filters: Filters) => void;
}

export interface State {
  filterOptions: Filters;
}

export interface FilterOption {
  count: number;
  label: string;
  selected: boolean;
  value: string;
}

export interface FilterOptions {
  [key: string]: FilterOption;
}

export interface Filter {
  displayCount: boolean;
  displayName: Map<any, string>;
  expanded: boolean;
  filterCategory: FilterCategory;
  label: string;
  metadata: string;
  options: FilterOptions;
  values: string[];
  mappedValues?: Map<any, string>;
  hide?: boolean;
}

export interface Filters {
  [key: string]: Filter;
}

class ContentFilters<T extends ContentFiltersProps> extends React.Component<T, State> {
  constructor(props: T) {
    super(props);
    this.state = {
      filterOptions: {
        // @ts-expect-error TS2322 - strict migration
        contentType: this.createFilterOption(FilterCategory.ContentType, contentTypeDisplayNames),
        // @ts-expect-error TS2322 - strict migration
        code: this.createFilterOption(FilterCategory.Code, null),
        // @ts-expect-error TS2322 - strict migration
        state: this.createFilterOption(FilterCategory.State, contentRequestStateDisplayNames),
        // @ts-expect-error TS2322 - strict migration
        requestType: this.createFilterOption(
          FilterCategory.RequestType,
          contentRequestTypeDisplayNames,
        ),
        // @ts-expect-error TS2322 - strict migration
        techDomains: this.createFilterOption(FilterCategory.TechDomains, TechDomainDisplayNames),
        // tslint:disable-next-line: object-literal-sort-keys
        // @ts-expect-error TS2322 - strict migration
        rType: this.createFilterOption(FilterCategory.RType, rTypeDisplayNames),
        // @ts-expect-error TS2322 - strict migration
        workload: this.createFilterOption(FilterCategory.Workload, workloadDisplayNames),
        // @ts-expect-error TS2322 - strict migration
        awsServices: this.createFilterOption(
          FilterCategory.AwsServices,
          awsServiceReadonlyDisplayNames,
        ),
        // @ts-expect-error TS2322 - strict migration
        environment: this.createFilterOption(FilterCategory.Environment, environmentDisplayNames),
        // @ts-expect-error TS2322 - strict migration
        authors: this.createFilterOption(FilterCategory.Authors, null),
        // @ts-expect-error TS2322 - strict migration
        publicationState: this.createFilterOption(
          FilterCategory.PublicationState,
          publicationStateDisplayNames,
          publicationStateVisibilityValues,
        ),
      },
    };
  }

  protected clearFilters = () => {
    const { filterOptions } = this.state;
    const { onFilterChanged } = this.props;
    Object.values(filterOptions).map((filter) => {
      if (filter) {
        filter.expanded = true;
        return Object.values(filter.options).forEach((option) => (option.selected = false));
      } else {
        return null;
      }
    });
    this.setState({
      filterOptions,
    });
    if (onFilterChanged) {
      onFilterChanged(filterOptions);
    }
  };

  protected expandAll = () => {
    const { filterOptions } = this.state;
    Object.values(filterOptions).forEach((filter) => {
      if (filter) {
        filter.expanded = true;
      }
    });
    this.setState({
      filterOptions,
    });
  };

  protected filterToSections = () => {
    const { filterOptions } = this.state;
    return Object.keys(filterOptions).map((filterSectionId) => {
      const filter = filterOptions[filterSectionId];
      return filter && Object.keys(filter.options).length ? (
        <ExpandableSection
          key={filterSectionId}
          expanded={filter.expanded}
          onChange={this.expandSection.bind(this, filter.filterCategory)}
          header={filter.label}
        >
          {this.optionsToCheckbox(filterSectionId, filter)}
        </ExpandableSection>
      ) : null;
    });
  };

  protected updateFilterOptionWithValue = (filterValue: string | [string], filter: Filter) => {
    if (filterValue) {
      if (typeof filterValue === 'string') {
        if (!filter.options[filterValue]) {
          filter.options[filterValue] = {
            count: 1,
            label: filter.displayName
              ? filter.displayName.get(filterValue) || filterValue
              : filterValue,
            selected: false,
            value: filterValue,
          };
        } else {
          filter.options[filterValue].count += 1;
        }
      } else {
        filterValue.forEach((option) => {
          if (!filter.options[option]) {
            filter.options[option] = {
              count: 1,
              label: filter.displayName ? filter.displayName.get(option) || option : option,
              selected: false,
              value: option,
            };
          } else {
            filter.options[option].count += 1;
          }
        });
      }
    }
  };

  private optionsToCheckbox = (filterSectionId: string, filter: Filter) => {
    const { options: filterOptions, displayCount } = filter;
    const filterOptionsKeys = this.getFilterOptionsKeys(filterOptions);
    return filterOptionsKeys.map((optionKey) => {
      const filterOption = filterOptions[optionKey];
      // @ts-expect-error TS18048 - strict migration
      const count = displayCount ? `(${filterOption.count})` : null;
      const padding: BoxProps.Spacing = [
        filterOptionToLabelMap.get(FilterCategory.RequireCode),
        // @ts-expect-error TS18048 - strict migration
      ].includes(filterOption.label)
        ? { left: 'xxl' }
        : { left: 'l' };
      return (
        <Box padding={padding} key={optionKey}>
          <Checkbox
            key={optionKey}
            // @ts-expect-error TS18048 - strict migration
            checked={filterOption.selected}
            // @ts-expect-error TS2322 - strict migration
            onChange={this.selectDeselectFilter.bind(this, filterSectionId, optionKey)}
          >
            {/* @ts-expect-error TS18048 - strict migration */}
            {filterOption.label} {count}
          </Checkbox>
        </Box>
      );
    });
  };

  private getFilterOptionsKeys = (filterOptions: FilterOptions) => {
    let filterOptionsKeys = Object.keys(filterOptions);
    if (filterOptionsKeys.includes(ContentType.Pattern)) {
      if (filterOptionsKeys.includes(FilterCategory.RequireCode)) {
        filterOptionsKeys = filterOptionsKeys.filter(
          (option) => option !== ContentType.Pattern && option !== FilterCategory.RequireCode,
        );
        filterOptionsKeys.unshift(FilterCategory.RequireCode);
        filterOptionsKeys.unshift(ContentType.Pattern);
      }
    }
    return filterOptionsKeys;
  };

  // @ts-expect-error TS7006 - strict migration
  private expandSection = (filter: string, e) => {
    e.preventDefault();
    const { filterOptions } = this.state;
    // @ts-expect-error TS2532 - strict migration
    filterOptions[filter].expanded = e.detail.expanded;
    this.setState({
      filterOptions,
    });
  };

  private selectDeselectFilter = (
    filterSectionId: string,
    filterOptionKey: string,
    event: CustomEvent<CheckboxProps.ChangeDetail>,
  ) => {
    const { filterOptions } = this.state;
    const { onFilterChanged } = this.props;
    const selected = event.detail.checked;
    const filterSection = filterOptions[filterSectionId];

    // @ts-expect-error TS18048 - strict migration
    if (filterSection.options[filterOptionKey]) {
      // @ts-expect-error TS18048 - strict migration
      filterSection.options[filterOptionKey].selected = selected;
      this.setState({
        filterOptions,
      });
      if (onFilterChanged) {
        onFilterChanged(filterOptions);
      }
    }
  };

  private createFilterOption = (
    filterCategory: FilterCategory,
    displayName: Map<string, string>,
    mappedValues?: Map<string, string>,
  ) => {
    const { filterOptionToMetadataMap, filterOptionToValuesMap } = this.props;
    return filterOptionToMetadataMap.has(filterCategory) &&
      filterOptionToValuesMap.has(filterCategory)
      ? {
          displayCount: false,
          displayName,
          expanded: true,
          filterCategory,
          label: filterOptionToLabelMap.get(filterCategory),
          metadata: filterOptionToMetadataMap.get(filterCategory),
          options: {},
          values: filterOptionToValuesMap.get(filterCategory),
          mappedValues,
        }
      : null;
  };
}

export default ContentFilters;
